import { Injectable, inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { AuthService } from '@services/auth/auth.service';
import { UserQueryService } from '@services/http-services/angular-query/userQueryService';

/**
 * List of routes that are exempt from onboarding redirection checks
 */
const EXEMPT_ROUTES = [
  '/organisation-admin-onboarding',
  '/500',
  '/login',
  '/registration',
  '/register',
];

/**
 * Guard that redirects organization admins to the onboarding flow if they haven't completed it
 * Uses modern functional guard pattern with dependency injection
 */
@Injectable({
  providedIn: 'root',
})
export class OnboardingRedirectGuard {
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly userQueryService = inject(UserQueryService);

  /**
   * Checks if the current route path is exempt from onboarding redirection
   * @param url The current URL to check
   * @returns True if the route is exempt, false otherwise
   */
  private _isRouteExempt(url: string): boolean {
    return EXEMPT_ROUTES.some(path => url.includes(path));
  }

  /**
   * Fetches the current user and determines if redirection is needed
   * Uses Angular Query to fetch user data
   * @returns Promise that resolves to true (allow) or a UrlTree (redirect)
   */
  private async _checkOnboardingStatus(): Promise<boolean | UrlTree> {
    const userUuid = this.authService.get_current_user_uuid();

    if (!userUuid) {
      return true;
    }

    try {
      const user = await this.userQueryService.fetchUserByUuid(userUuid);
      const isOrgAdmin = this.authService.is_org_admin();

      if (!user.is_onboarding_complete && isOrgAdmin) {
        return this.router.parseUrl('/organisation-admin-onboarding');
      }

      return true;
    } catch {
      return true;
    }
  }

  /**
   * Main guard function that determines if a route can be activated
   * @param route The route being activated
   * @param state The router state
   * @returns Promise of boolean or UrlTree indicating if navigation should proceed or redirect
   */
  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Promise<boolean | UrlTree> | boolean | UrlTree {
    if (this._isRouteExempt(state.url)) {
      return true;
    }

    return this._checkOnboardingStatus();
  }
}

export const onboardingRedirectGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  return inject(OnboardingRedirectGuard).canActivate(route, state);
};
